import { useCallback, useEffect, useState } from "react";
import { ZeroAddress } from "ethers";

import ApiRoutes from "../../config/api.routes";
import AxiosFactory from "../../services/http.service";
import {
  getContract,
  getMetaMaskSigner,
  getReadOnlyContract,
  getReadOnlyProvider,
} from "../../services/contract.service";
import { isUserRejection, relayContractCall } from "../../services/relay.service";
import { describeChainError } from "../../services/challenge.service";
import { EcoTokenStatus } from "../../utils/constants";

// Refund an untaken verification request (V10.1.2, EcoChallenge guide §3d). A request nobody
// accepted can now be refunded to its guardian as a type 1 release —
// EcoFuturesAdmin.releaseVerificationFunds(requestId, 1, 0x0) — once the acceptance window has
// passed, by a Trust Admin of the request's country or a GTA. Before V10.1.2 that call reverted
// NotYetAccepted for any request without a verifier, so the fee sat in escrow for good.
//
// Chain-only on purpose. No REST surface lists untaken requests to an admin (the Portal's
// /trust/unverifiedTokens only returns CLAIMED rows, i.e. ones a verifier already took), but
// request ids are a dense counter (getNextRequestId returns the last one issued), so the tail of
// it IS the recent set — the same trick listRecentChallengeSubjects uses for challenges.
//
// Refundable from requestedAt + verifierAcceptanceWindow. The V10.1 source in docs/ predates this
// path, so that deadline is inferred from the guide; every click is dry-run first (staticCall
// from the viewer) so the contract, not this guess, has the last word and names any revert.
//
// Transport: a Trust Admin goes through the relay (gasless, "holder" lane). A GTA cannot — the
// relay only admits holders for this function and V10.1.2 refuses to make a GTA a holder
// (GTACannotBeHolder) — so a GTA sends it from their own wallet and pays the gas.
//
// The requests are rows of the Portal's Ecofutures table, so each is joined to its land (see
// joinLands). A request whose land cannot be found still gets a row — the refund is a chain
// action and needs nothing from REST.

const SCAN_LIMIT = 100;

// Every status, because nothing pins where the land of an untaken request sits: the open pool
// (VERIFICATION_REQUIRED), an off-chain claim that never reached the chain (CLAIMED), a
// reassignment, or a minted land's re-verification. eco_token.status is a varchar, so a value
// the backend no longer uses just matches nothing.
const ALL_STATUSES = Array.from(new Set(Object.values(EcoTokenStatus))).join(",");

export interface UntakenRequest {
  requestId: number;
  guardian: string;
  feeAmount: bigint;
  countryCode: number;
  requestedAt: Date;
  refundableAt: Date;
}

// A table row: the land's own eco-token fields, flagged, with the request beside them. The request
// is namespaced under `untaken` rather than spread, because eco_token has its own requestId and
// feeAmount columns (as strings) and the chain's values must not be confused with the mirror's.
export type UntakenRequestRow = Record<string, any> & {
  id: number | string | null;
  isUntakenRequest: true;
  // False when no eco-token row carried this requestId: nothing to link to or inspect.
  landFound: boolean;
  untaken: UntakenRequest;
};

// No REST route keys a land by its request id, but the land's own row carries it
// (eco_token.requestId, written off VerificationRequested) and GET /eco-token?mintedBy= returns
// full rows — and the request's guardian IS the minter. So: one list call per distinct guardian,
// matched on requestId. (VerificationRequested also carries internalTokenId, but the public BSC
// testnet RPC answers an unbounded getLogs with nothing, so the chain cannot be asked.)
const joinLands = async (requests: UntakenRequest[]): Promise<UntakenRequestRow[]> => {
  const axios = new AxiosFactory().axios;
  const guardians = Array.from(new Set(requests.map((r) => r.guardian.toLowerCase())));
  const landsByRequest: Record<string, any> = {};
  await Promise.all(
    guardians.map(async (guardian) => {
      try {
        const lands = await axios
          .get(`${ApiRoutes.token.minted(guardian)}&status=${ALL_STATUSES}&limit=50`)
          .then((res) => res.data);
        (Array.isArray(lands) ? lands : []).forEach((land: any) => {
          if (land?.requestId !== undefined && land?.requestId !== null) {
            landsByRequest[String(land.requestId)] = land;
          }
        });
      } catch (e) {
        console.warn("untaken request land join failed; showing the chain rows only", guardian, e);
      }
    })
  );
  return requests.map((request) => {
    const land = landsByRequest[String(request.requestId)];
    return {
      ...(land ?? {}),
      id: land?.id ?? null,
      isUntakenRequest: true,
      landFound: !!land,
      untaken: request,
    };
  });
};

// `viewerAddress` scopes the scan (a Trust Admin sees their own country's requests, a GTA every
// country's); `onRefunded` runs after a refund lands, so the Portal can refresh whatever it caches.
const useUntakenRequests = (viewerAddress?: string | null, onRefunded?: () => void) => {
  const [rows, setRows] = useState<UntakenRequestRow[]>([]);
  const [viewerIsGta, setViewerIsGta] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [error, setError] = useState<{ requestId: number; message: string } | null>(null);
  const [chainOffsetMs, setChainOffsetMs] = useState(0);

  const load = useCallback(async () => {
    const viewer = viewerAddress?.toLowerCase();
    const admin = getReadOnlyContract("ecofutures-admin");
    if (!viewer || !admin) {
      setRows([]);
      return;
    }
    try {
      const [gta, holder, holderCountry, lastId, windowSec, latest] = await Promise.all([
        admin.isGTA(viewer).then(Boolean),
        admin.isHolder(viewer).then(Boolean),
        admin.getHolderCountry(viewer).then(Number),
        admin.getNextRequestId().then(Number),
        admin.verifierAcceptanceWindow().then(Number),
        getReadOnlyProvider()
          .getBlock("latest")
          .catch(() => null),
      ]);
      setViewerIsGta(gta);
      if (latest) {
        setChainOffsetMs(Number(latest.timestamp) * 1000 - Date.now());
      }
      if (!gta && !holder) {
        setRows([]);
        return;
      }
      const ids: number[] = [];
      for (let id = lastId; id > Math.max(0, lastId - SCAN_LIMIT); id--) {
        ids.push(id);
      }
      const found: UntakenRequest[] = [];
      for (let i = 0; i < ids.length; i += 20) {
        const batch = ids.slice(i, i + 20);
        const reqs = await Promise.all(batch.map((id) => admin.getVerificationRequest(id)));
        reqs.forEach((r: any, j: number) => {
          const requestedAt = Number(r.requestedAt);
          const countryCode = Number(r.countryCode);
          const untaken = requestedAt > 0 && r.verifier === ZeroAddress && !r.fulfilled && !r.fundsReleased;
          // A GTA may refund any country's; a Trust Admin their own (a global holder, country 0, any).
          const inScope = gta || holderCountry === 0 || holderCountry === countryCode;
          if (untaken && inScope) {
            found.push({
              requestId: batch[j],
              guardian: String(r.guardian),
              feeAmount: BigInt(r.feeAmount),
              countryCode,
              requestedAt: new Date(requestedAt * 1000),
              refundableAt: new Date((requestedAt + windowSec) * 1000),
            });
          }
        });
      }
      setRows(found.length ? await joinLands(found) : []);
    } catch (e) {
      console.warn("untaken request scan failed:", e);
      setRows([]);
    }
  }, [viewerAddress]);

  useEffect(() => {
    load();
  }, [load]);

  // Judged against the chain's clock, not the browser's: the window is a block-timestamp deadline.
  const isRefundable = (row: UntakenRequestRow) => row.untaken.refundableAt.getTime() <= Date.now() + chainOffsetMs;

  const refund = async (row: UntakenRequestRow) => {
    const viewer = viewerAddress;
    const admin = getReadOnlyContract("ecofutures-admin");
    const { requestId } = row.untaken;
    if (!viewer || !admin) {
      return;
    }
    setBusyId(requestId);
    setError(null);
    try {
      // Dry run from the viewer first: the relay path and MetaMask both lose revert data, the
      // read-only provider does not, so this is where a refusal gets its name.
      await admin.releaseVerificationFunds.staticCall(requestId, 1, ZeroAddress, { from: viewer });
      if (viewerIsGta) {
        const signer = await getMetaMaskSigner();
        const writable = getContract("ecofutures-admin", signer);
        if (!writable) {
          throw new Error("EcoFuturesAdmin contract not found");
        }
        const tx = await writable.releaseVerificationFunds(requestId, 1, ZeroAddress);
        const receipt = await tx.wait();
        if (!receipt || receipt.status !== 1) {
          throw new Error("The refund transaction failed");
        }
      } else {
        await relayContractCall(
          "ecofutures-admin",
          "releaseVerificationFunds",
          [requestId, 1, ZeroAddress],
          "VerificationFundsReleased",
          "Refund untaken request"
        );
      }
      setRows((prev) => prev.filter((r) => r.untaken.requestId !== requestId));
      onRefunded?.();
    } catch (e: any) {
      if (!isUserRejection(e)) {
        setError({ requestId, message: describeChainError(e) });
      }
    } finally {
      setBusyId(null);
    }
  };

  return { rows, viewerIsGta, busyId, error, isRefundable, refund };
};

export default useUntakenRequests;
